import type { TFunction } from 'i18next'
import { sdk } from '@/lib/sdk'
import type { CompanyValue, RecognitionValuesConfig } from '@/types/recognition'

/** The API's limits (`recognition.service.js` LIMITS); the server checks them again. */
export const RECOGNITION_LIMITS = Object.freeze({
  message: 500,
  recipients: 20,
  comment: 2000,
  valueName: 80,
  valueDescription: 300,
  values: 30
})

export type RecognitionValuesErrors = { values?: string } | null

/** Values are optional: a new space starts with none. */
export const defaultRecognitionValuesConfig = (): RecognitionValuesConfig => ({ values: [] })

export const valuesConfigFrom = (rows: CompanyValue[]): RecognitionValuesConfig => ({
  values: rows.map((v) => ({ id: v.id, name: v.name, description: v.description ?? '', active: v.active }))
})

export const validateRecognitionValues = (config: RecognitionValuesConfig | null | undefined, t: TFunction): RecognitionValuesErrors => {
  const values = config?.values ?? []
  if (values.some((v) => !v.name.trim())) return { values: t('recognition:values.errors.nameRequired') }
  if (values.length > RECOGNITION_LIMITS.values) return { values: t('recognition:values.errors.tooMany', { max: RECOGNITION_LIMITS.values }) }
  return null
}

/** The field error of a refused save (`param` name or description), or null. */
export const valuesErrorsFromApi = (err: unknown): RecognitionValuesErrors => {
  const data = (err as { response?: { data?: { message?: string; errors?: Array<{ param?: string; message?: string }> } } })?.response?.data
  const hit = data?.errors?.find((e) => e.param === 'name' || e.param === 'description' || e.param === 'ids')
  return hit ? { values: hit.message || data?.message || '' } : null
}

/**
 * Writes the edited list: creates the new ones, updates what changed, then
 * saves the order. Nothing is ever deleted (the API keeps every value, a
 * removed one is deactivated), so the order covers every value it knows.
 */
export const saveRecognitionValues = async (config: RecognitionValuesConfig | null | undefined, spaceId?: string): Promise<CompanyValue[]> => {
  const wanted = config?.values ?? []
  const current = await sdk.recognitions.listValues(true, spaceId)
  const byId = new Map(current.map((v) => [v.id, v]))
  const ids: string[] = []
  for (const v of wanted) {
    const name = v.name.trim()
    const description = v.description.trim() || null
    if (!v.id) {
      const created = await sdk.recognitions.createValue({ name, description })
      if (!v.active) {
        await sdk.recognitions.updateValue(created.id, { active: false })
      }
      ids.push(created.id)
      continue
    }
    const before = byId.get(v.id)
    if (before && (before.name !== name || (before.description ?? null) !== description || before.active !== v.active)) {
      await sdk.recognitions.updateValue(v.id, { name, description, active: v.active })
    }
    ids.push(v.id)
  }
  const known = new Set(ids)
  const order = [...ids, ...current.filter((v) => !known.has(v.id)).map((v) => v.id)]
  const changedOrder = order.length !== current.length || order.some((id, i) => current[i]?.id !== id)
  if (order.length > 1 && changedOrder) return sdk.recognitions.reorderValues(order)
  return sdk.recognitions.listValues(true, spaceId)
}

/** The "N people" of a recognition: one avatar row, names joined by the locale's list format. */
export const joinNames = (names: string[], language: string): string => {
  /* `Intl.ListFormat` is ES2021; the target's lib is ES2020, so it is reached untyped. */
  const ListFormat = (Intl as unknown as { ListFormat?: new (l: string, o: object) => { format: (items: string[]) => string } }).ListFormat
  try {
    if (ListFormat) return new ListFormat(language, { style: 'long', type: 'conjunction' }).format(names)
    return names.join(', ')
  } catch {
    return names.join(', ')
  }
}

/**
 * A list in the locale's format, as parts (F-185 sentence): `element` parts are
 * the items, in order, so each name can be its own link.
 */
export const listParts = (items: string[], language: string): Array<{ type: 'element' | 'literal'; value: string }> => {
  const ListFormat = (Intl as unknown as { ListFormat?: new (l: string, o: object) => { formatToParts: (items: string[]) => Array<{ type: 'element' | 'literal'; value: string }> } }).ListFormat
  try {
    if (ListFormat) return new ListFormat(language, { style: 'long', type: 'conjunction' }).formatToParts(items)
  } catch {
    /* fall through to the plain join */
  }
  return items.flatMap((value, i) => (i === 0 ? [{ type: 'element' as const, value }] : [{ type: 'literal' as const, value: ', ' }, { type: 'element' as const, value }]))
}
